import { ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DisclosureExamples } from '../progress/DisclosureExamples';
import { LiveStepsDemo } from '../progress/LiveStepsDemo';
import { Code, Section } from './Section';
import { CompactStepsTile } from './CompactStepsTile';

export function ProgressPatternsSection() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (hash !== '#progress-patterns') return;
    const section = document.getElementById('progress-patterns');
    const main = section?.closest('main');
    const root = main?.parentElement;
    if (!section || !main || !root) return;
    let active = true;
    const align = () =>
      requestAnimationFrame(() => {
        if (!active || window.location.hash !== '#progress-patterns') return;
        const header = root.querySelector('header')?.getBoundingClientRect().height ?? 0;
        root.scrollTo({
          top:
            root.scrollTop +
            section.getBoundingClientRect().top -
            root.getBoundingClientRect().top -
            header -
            24,
          behavior: 'instant',
        });
      });
    const observer = new ResizeObserver(align);
    const stop = () => {
      active = false;
      observer.disconnect();
      root.removeEventListener('scroll', align);
      for (const event of ['wheel', 'pointerdown', 'keydown']) root.removeEventListener(event, stop, true);
    };
    observer.observe(main);
    root.addEventListener('scroll', align, { passive: true });
    for (const event of ['wheel', 'pointerdown', 'keydown']) root.addEventListener(event, stop, true);
    align();
    void document.fonts.ready.then(align);
    return stop;
  }, [hash]);
  const onEntityOpen = (id: string) => navigate('/design/progress/object/' + id);
  return (
    <Section
      id="progress-patterns"
      title="Раскрытия и шаги"
      lead="Общие элементы для любых сервисов: подробности по запросу и понятный ход фоновой операции. Данные вымышлены."
    >
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <header className="flex flex-wrap items-center gap-4 border-b border-line p-5">
          <div className="min-w-0 flex-1">
            <h3 className="type-heading">Раскрывающиеся блоки</h3>
            <p className="mt-1 text-sm text-fg-muted">
              Один блок, группа и строки списка. Enter и пробел раскрывают; стрелки, Home и End перемещают
              фокус внутри группы.
            </p>
          </div>
          <Link
            to="/design/progress"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-muted px-3 text-xs font-semibold text-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            Открыть образец
            <ArrowRight className="size-4" />
          </Link>
        </header>
        <div className="p-5">
          <DisclosureExamples scope="gallery" onEntityOpen={onEntityOpen} />
        </div>
        <p className="border-t border-line p-4">
          <Code>Disclosure · Accordion · useExpansionMemory</Code>
        </p>
      </div>
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <header className="border-b border-line p-5">
          <h3 className="type-heading">Живой список шагов</h3>
          <p className="mt-1 text-sm text-fg-muted">
            Что сделано, что идёт, что ждёт и где произошёл сбой. Подробности и ссылки — в каждом шаге.
          </p>
        </header>
        <div className="p-5">
          <LiveStepsDemo scope="gallery" onEntityOpen={onEntityOpen} />
        </div>
        <p className="border-t border-line p-4">
          <Code>LiveStepList · StepProgress · Disclosure · EntityChip</Code>
        </p>
      </div>
      <CompactStepsTile />
    </Section>
  );
}
